var { initIframePrint } = require('./shared/iframe-print');

/**
 * Monthly Planner - Interactive Calendar Management
 * Handles calendar rendering, event management, and goal tracking
 */

(function() {
    'use strict';

    let currentDate = new Date();
    let selectedDate = new Date();
    let events = JSON.parse(localStorage.getItem('monthlyEvents') || '[]');
    const goalsStorageKey = 'monthlyPlannerGoals';
    const notesStorageKey = 'monthlyPlannerNotes';

    /**
     * Initialize the monthly planner
     */
    function init() {
        initIframePrint({
            formId: 'mpPrintForm',
            iframeId: 'mpPrintFrame',
            buttonId: 'mpPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Calendar',
        });

        restoreScrollPosition();
        const today = new Date();
        currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
        selectedDate = today;
        document.getElementById('eventDate').value = formatDateValue(today);
        renderCalendar();
        
        // Event listeners
        document.getElementById('eventText').addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
                window.MonthlyPlanner.addEvent();
            }
        });

        var addFirstEventBtn = document.getElementById('btn-add-first-event');
        if (addFirstEventBtn) {
            addFirstEventBtn.addEventListener('click', function(e) {
                e.preventDefault();
                document.getElementById('eventText').focus();
            });
        }

        const eventDateInput = document.getElementById('eventDate');
        eventDateInput.addEventListener('change', function() {
            if (!this.value) {
                return;
            }
            const parsedDate = new Date(this.value + 'T00:00:00');
            if (isNaN(parsedDate.getTime())) {
                return;
            }
            selectedDate = parsedDate;
            currentDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
            renderCalendar();
        });

        // Placeholder behavior for contenteditable elements
        document.querySelectorAll('[contenteditable="true"][data-placeholder]').forEach(el => {
            if (!el.textContent.trim()) {
                el.classList.add('placeholder-visible');
            }
            el.addEventListener('focus', function() {
                this.classList.remove('placeholder-visible');
            });
            el.addEventListener('blur', function() {
                if (!this.textContent.trim()) {
                    this.classList.add('placeholder-visible');
                }
            });
        });

        loadGoalsAndNotes();
        attachGoalsAndNotesHandlers();

        // Attach event handlers to buttons
        attachEventHandlers();

        const monthlyPlannerForm = document.getElementById('monthlyPlannerForm');
        if (monthlyPlannerForm) {
            monthlyPlannerForm.addEventListener('submit', saveGoalsAndNotes);
            monthlyPlannerForm.addEventListener('submit', saveScrollPosition);
        }

        initClearConfirmation();
    }

    /**
     * Attach event handlers to buttons
     */
    function attachEventHandlers() {
        // Month navigation buttons
        const prevMonthBtn = document.querySelector('[data-action="prev-month"]');
        const nextMonthBtn = document.querySelector('[data-action="next-month"]');
        const todayBtn = document.querySelector('[data-action="today"]');
        
        if (prevMonthBtn) {
            prevMonthBtn.addEventListener('click', () => changeMonth(-1));
        }
        if (nextMonthBtn) {
            nextMonthBtn.addEventListener('click', () => changeMonth(1));
        }
        if (todayBtn) {
            todayBtn.addEventListener('click', goToCurrentMonth);
        }

        // Add event button
        const addEventBtn = document.querySelector('[data-action="add-event"]');
        if (addEventBtn) {
            addEventBtn.addEventListener('click', addEvent);
        }
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('monthlyPlannerClear');
        const overlay = document.getElementById('clearConfirmationOverlay');
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');
        const confirmButton = overlay.querySelector('[data-clear-confirmation="confirm"]');

        clearButton.addEventListener('click', function () {
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });

        if (confirmButton) {
            confirmButton.addEventListener('click', function () {
                closeClearConfirmation(overlay);
                clearAllEvents();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Navigate to current month
     */
    function goToCurrentMonth() {
        const today = new Date();
        currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
        selectedDate = today;
        document.getElementById('eventDate').value = formatDateValue(today);
        renderCalendar();
    }

    /**
     * Change month by delta
     * @param {number} delta - Number of months to move (-1 for previous, 1 for next)
     */
    function changeMonth(delta) {
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1);
        renderCalendar();
    }

    /**
     * Render the calendar for current month
     */
    function renderCalendar() {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        document.getElementById('monthTitle').textContent = `${monthNames[month]} ${year}`;

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startingDay = firstDay.getDay();
        const totalDays = lastDay.getDate();

        const grid = document.getElementById('calendarGrid');
        grid.innerHTML = '';

        // Empty cells before first day
        for (let i = 0; i < startingDay; i++) {
            const emptyCell = document.createElement('div');
            emptyCell.className = 'calendar-day empty';
            grid.appendChild(emptyCell);
        }

        // Day cells
        const today = new Date();
        const selectedDateValue = formatDateValue(selectedDate);
        for (let day = 1; day <= totalDays; day++) {
            const dayCell = document.createElement('div');
            dayCell.className = 'calendar-day';
            
            const isToday = day === today.getDate() && 
                           month === today.getMonth() && 
                           year === today.getFullYear();
            if (isToday) {
                dayCell.classList.add('today');
            }

            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = dateStr === selectedDateValue;
            if (isSelected) {
                dayCell.classList.add('selected');
            }
            const dayEvents = events.filter(e => e.date === dateStr);

            dayCell.innerHTML = `
                <div class="day-number ${isToday ? 'today' : ''}${isSelected ? ' selected' : ''}">${day}</div>
                <div class="day-events">
                    ${dayEvents.map(e => `
                        <div class="event-item bg-${e.color}" title="${escapeHtml(e.text)}">
                            ${escapeHtml(e.text)}
                            <button class="event-delete" data-event-id="${e.id}">&times;</button>
                        </div>
                    `).join('')}
                </div>
            `;

            dayCell.onclick = (e) => {
                if (!e.target.classList.contains('event-delete')) {
                    selectedDate = new Date(dateStr + 'T00:00:00');
                    document.getElementById('eventDate').value = dateStr;
                    renderCalendar();
                    document.getElementById('eventText').focus();
                } else {
                    const eventId = e.target.getAttribute('data-event-id');
                    window.MonthlyPlanner.deleteEvent(eventId);
                }
            };

            grid.appendChild(dayCell);
        }

        saveEvents();
    }

    /**
     * Add a new event
     */
    function addEvent() {
        const date = document.getElementById('eventDate').value;
        const text = document.getElementById('eventText').value.trim();
        const color = document.getElementById('eventColor').value;

        if (!date || !text) {
            return;
        }

        events.push({
            id: Date.now().toString(),
            date: date,
            text: text,
            color: color
        });

        document.getElementById('eventText').value = '';
        renderCalendar();
    }

    /**
     * Delete an event
     * @param {string} id - Event ID to delete
     */
    function deleteEvent(id) {
        events = events.filter(e => e.id !== id);
        renderCalendar();
    }

    /**
     * Clear all events
     */
    function clearAllEvents() {
        events = [];
        localStorage.removeItem('monthlyEvents');
        localStorage.removeItem(goalsStorageKey);
        localStorage.removeItem(notesStorageKey);
        document.querySelectorAll('.goals-list input[type="checkbox"]').forEach(checkbox => {
            checkbox.checked = false;
        });
        document.querySelectorAll('.goals-list .goal-text').forEach(goal => {
            goal.textContent = '';
            goal.classList.add('placeholder-visible');
        });
        const notesArea = document.querySelector('.notes-area');
        if (notesArea) {
            notesArea.textContent = '';
            notesArea.classList.add('placeholder-visible');
        }
        renderCalendar();
    }

    function loadGoalsAndNotes() {
        try {
            const storedGoals = JSON.parse(localStorage.getItem(goalsStorageKey) || '[]');
            document.querySelectorAll('.goals-list .goal-item').forEach((item, index) => {
                const goal = storedGoals[index];
                const checkbox = item.querySelector('input[type="checkbox"]');
                const text = item.querySelector('.goal-text');
                if (checkbox) {
                    checkbox.checked = !!goal?.done;
                }
                if (text) {
                    text.textContent = goal?.text || '';
                    if (text.textContent.trim()) {
                        text.classList.remove('placeholder-visible');
                    } else {
                        text.classList.add('placeholder-visible');
                    }
                }
            });

            const notesArea = document.querySelector('.notes-area');
            if (notesArea) {
                const notesText = localStorage.getItem(notesStorageKey) || '';
                notesArea.textContent = notesText;
                if (notesText.trim()) {
                    notesArea.classList.remove('placeholder-visible');
                } else {
                    notesArea.classList.add('placeholder-visible');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    function saveScrollPosition() {
        const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
        if (modalOverlay) {
            return;
        }

        try {
            sessionStorage.setItem('monthlyPlannerScrollPos', window.pageYOffset.toString());
            sessionStorage.setItem('monthlyPlannerScrollX', window.pageXOffset.toString());
        } catch (e) {
            // Ignore storage errors
        }
    }

    function restoreScrollPosition() {
        try {
            const scrollPos = sessionStorage.getItem('monthlyPlannerScrollPos');
            const scrollX = sessionStorage.getItem('monthlyPlannerScrollX');
            if (scrollPos) {
                requestAnimationFrame(() => {
                    window.scrollTo(parseInt(scrollX || '0', 10), parseInt(scrollPos, 10));
                });

                const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
                if (!modalOverlay) {
                    sessionStorage.removeItem('monthlyPlannerScrollPos');
                    sessionStorage.removeItem('monthlyPlannerScrollX');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    function attachGoalsAndNotesHandlers() {
        document.querySelectorAll('.goals-list .goal-item').forEach(item => {
            const checkbox = item.querySelector('input[type="checkbox"]');
            const text = item.querySelector('.goal-text');
            if (checkbox) {
                checkbox.addEventListener('change', saveGoalsAndNotes);
            }
            if (text) {
                text.addEventListener('input', saveGoalsAndNotes);
            }
        });

        const notesArea = document.querySelector('.notes-area');
        if (notesArea) {
            notesArea.addEventListener('input', saveGoalsAndNotes);
        }
    }

    function saveGoalsAndNotes() {
        try {
            const goals = Array.from(document.querySelectorAll('.goals-list .goal-item')).map(item => {
                const checkbox = item.querySelector('input[type="checkbox"]');
                const text = item.querySelector('.goal-text');
                return {
                    done: checkbox ? checkbox.checked : false,
                    text: text ? text.textContent.trim() : ''
                };
            });
            localStorage.setItem(goalsStorageKey, JSON.stringify(goals));

            const notesArea = document.querySelector('.notes-area');
            if (notesArea) {
                localStorage.setItem(notesStorageKey, notesArea.textContent.trim());
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Save events to localStorage
     */
    function saveEvents() {
        localStorage.setItem('monthlyEvents', JSON.stringify(events));
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} - Escaped text
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    function formatDateValue(date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose public API
    window.MonthlyPlanner = {
        goToCurrentMonth: goToCurrentMonth,
        changeMonth: changeMonth,
        addEvent: addEvent,
        deleteEvent: deleteEvent,
        clearAllEvents: clearAllEvents
    };

})();
